import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  TextField,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  CircularProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { debounce } from "lodash";
import moment from "moment";
import { BIBlue, BIGrey } from "../../assets/helpers";
import { setLogindata, setShowmapmodal, setSelectedsite, setChoosesite, setSelectedProjectdata } from "../../redux/actions/actions";
import { Postrequestcall } from "../../request/Postrequest";
import {
  GET_ALL_TRADES_USING_USER_ID,
  SAVE_USER_TRADES,
  FETCH_COMPANIES_BY_NAME_NEW,
  SAVE_COMPANIES,
  SAVE_USER_COMPANIES,
  SAVE_USER_WORK_HISTORY
} from "../../constant/Apipath";
import BuildIDLogo from "../../assets/images/build_id_logo.png";
import Mapmodal from "../common/Mapmodal";
import PlaceholderImg from "../../assets/images/placeholder_img.jpg";
import CompanySelectModal from "../common/CompanySelectModal";

const OnboardingWizard = ({ open, closeOnboarding }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loginData, selectedProject } = useSelector((state) => state?.main);
  const rawLoginData = useSelector((state) => state.main?.loginData);
  const { sourceDataFrom, ...filteredLoginData } = rawLoginData || {};

  const [step, setStep] = useState(1); // 1 = Trades, 2 = Company, 3 = Project

  // Trades step states
  const [allTrades, setAllTrades] = useState([]);
  const [tradeSearchQuery, setTradeSearchQuery] = useState("");
  const [selectedTradeIds, setSelectedTradeIds] = useState([]);
  const [loadingTrades, setLoadingTrades] = useState(false);
  const [tradePage, setTradePage] = useState(1);
  const [hasMoreTrades, setHasMoreTrades] = useState(true);

  // Company step states
  const [companySearchQuery, setCompanySearchQuery] = useState("");
  const [companiesList, setCompaniesList] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [companyPage, setCompanyPage] = useState(1);
  const [hasMoreCompanies, setHasMoreCompanies] = useState(true);
  const [openCompanyModal, setOpenCompanyModal] = useState(false);

  // Date states for project experience
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [present, setPresent] = useState(true);

  // Date states for company experience
  const [companyStartDate, setCompanyStartDate] = useState("");
  const [companyEndDate, setCompanyEndDate] = useState("");
  const [companyPresent, setCompanyPresent] = useState(true);

  // Submission/loading states
  const [savingAll, setSavingAll] = useState(false);

  useEffect(() => {
    if (open) {
      setTradePage(1);
      setHasMoreTrades(true);
      fetchTrades("", 1);
      dispatch(setSelectedProjectdata(null));
    }
  }, [open]);

  useEffect(() => {
    if (selectedProject) {
      if (selectedProject.StartDate || selectedProject.startDate) {
        setStartDate(moment(selectedProject.StartDate || selectedProject.startDate).format("YYYY-MM-DD"));
      } else {
        setStartDate("");
      }
      if (selectedProject.EndDate || selectedProject.endDate) {
        setEndDate(moment(selectedProject.EndDate || selectedProject.endDate).format("YYYY-MM-DD"));
        setPresent(false);
      } else {
        setEndDate("");
        setPresent(true);
      }
    } else {
      setStartDate("");
      setEndDate("");
      setPresent(true);
    }
  }, [selectedProject]);

  const handleClose = () => {
    localStorage.removeItem("onboardingPending");
    if (closeOnboarding) closeOnboarding();
  };

  const handleSkip = () => {
    handleClose();
    dispatch(setLogindata(filteredLoginData));
  };

  // FETCH TRADES
  const fetchTrades = async (search = "", page = 1) => {
    setLoadingTrades(true);
    try {
      const obj = {
        PageNumber: page,
        UserId: loginData?.userInformation?.UserId,
        NrRowsPerPage: 40,
        SearchString: search
      };
      const response = await Postrequestcall(
        GET_ALL_TRADES_USING_USER_ID,
        obj,
        loginData?.accessToken
      );
      if (response.status === 200) {
        const data = response?.data?.data || [];
        if (page === 1) {
          setAllTrades(data);
        } else {
          setAllTrades((prev) => [...prev, ...data]);
        }
        if (data.length < 40) {
          setHasMoreTrades(false);
        } else {
          setHasMoreTrades(true);
        }
      }
    } catch (err) {
      console.error("Error fetching trades:", err);
    } finally {
      setLoadingTrades(false);
    }
  };

  const loadMoreTrades = () => {
    const nextPage = tradePage + 1;
    setTradePage(nextPage);
    fetchTrades(tradeSearchQuery, nextPage);
  };

  const debouncedFetchTrades = useCallback(
    debounce((query) => {
      setTradePage(1);
      fetchTrades(query, 1);
    }, 400),
    [loginData]
  );

  const handleTradeSearchChange = (e) => {
    const val = e.target.value;
    setTradeSearchQuery(val);
    debouncedFetchTrades(val);
  };

  // FETCH COMPANIES
  const fetchCompanies = async (search, page = 1) => {
    if (!search || search.trim().length < 2) {
      setCompaniesList([]);
      return;
    }
    setLoadingCompanies(true);
    try {
      const obj = {
        PageNr: page,
        NrOfRecPerPage: 20,
        FullSearch: search,
        UserId: loginData?.userInformation?.UserId ?? 0,
        IncludeRecordNr: "true",
        TypeOfObjectReturned: "",
        FetchAllowedRecordsOnly: true,
        SearchList: [{}],
        SortList: [
          { FieldName: "Verified", Direction: "DESC" },
          { FieldName: "Name", Direction: "ASC" }
        ]
      };
      const res = await Postrequestcall(FETCH_COMPANIES_BY_NAME_NEW, obj, loginData?.accessToken);
      if (res.status === 200 || res.status === 201) {
        const data = res?.data?.data || [];
        if (page === 1) {
          setCompaniesList(data);
        } else {
          setCompaniesList((prev) => [...prev, ...data]);
        }
        if (data.length < 20) {
          setHasMoreCompanies(false);
        } else {
          setHasMoreCompanies(true);
        }
      }
    } catch (err) {
      console.error("Error fetching companies:", err);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const loadMoreCompanies = () => {
    const nextPage = companyPage + 1;
    setCompanyPage(nextPage);
    fetchCompanies(companySearchQuery, nextPage);
  };

  const debouncedFetchCompanies = useCallback(
    debounce((query) => {
      setCompanyPage(1);
      fetchCompanies(query, 1);
    }, 400),
    [loginData]
  );

  const handleCompanySearchChange = (e) => {
    const val = e.target.value;
    setCompanySearchQuery(val);
    debouncedFetchCompanies(val);
  };

  // SAVE USER COMPANY
  const saveUserCompany = async (companyId) => {
    const userId = Number(loginData?.userInformation?.UserId);
    const userCompanyObj = {
      userId: userId,
      saveList: [
        {
          Id: 0,
          UserId: userId,
          CompanyId: companyId,
          StartDate: companyStartDate ? companyStartDate : null,
          endDate: companyPresent ? null : companyEndDate ? companyEndDate : null,
          CurrentCompany: companyPresent,
          IncludeInResume: true,
          CreatedBy: userId,
          ModifiedBy: userId
        }
      ]
    };
    await Postrequestcall(SAVE_USER_COMPANIES, userCompanyObj, loginData?.accessToken);
  };

  // OPEN SITE MAP MODAL
  const handleSelectSiteClick = () => {
    dispatch(
      setShowmapmodal({
        open: true,
        from: "experience",
        select: "project"
      })
    );
  };

  // SUBMIT ALL ONBOARDING DATA AT THE END
  const handleOnboardingFinish = async () => {
    if (selectedCompany && !companyStartDate) {
      alert("Please select a Start Date for your company.");
      return;
    }
    if (selectedProject && !startDate) {
      alert("Please select a Start Date for your project.");
      return;
    }
    setSavingAll(true);
    const userId = Number(loginData?.userInformation?.UserId);
    let finalCompanyId = 0;

    try {
      // 1. Save Trades if selected
      if (selectedTradeIds.length > 0) {
        try {
          const saveList = selectedTradeIds.map((id, index) => {
            const trade = allTrades.find((t) => t.id === id);
            return {
              Id: 0,
              UserId: userId,
              TradeId: id,
              TradeType: index === 0 ? "Main" : "Other",
              ModifiedBy: userId,
              tradeName: trade?.tradeName
            };
          });
          const saveObj = {
            UserId: userId,
            ReturnRecordId: true,
            ReturnRecordError: true,
            SaveList: saveList
          };
          await Postrequestcall(SAVE_USER_TRADES, saveObj, loginData?.accessToken);
          const mainTrade = allTrades.find((t) => t.id === selectedTradeIds[0]);
          if (mainTrade?.tradeName) {
            localStorage.setItem("tradeName", mainTrade.tradeName.toString());
          }
        } catch (tradeError) {
          console.error("Onboarding - Error saving trades:", tradeError);
        }
      }

      // 2. Save Company if selected
      if (selectedCompany) {
        try {
          if (selectedCompany.id === 0) {
            const saveCompanyObj = {
              userId,
              ReturnRecordId: true,
              ReturnRecordError: true,
              SaveList: [
                {
                  Id: 0,
                  Name: selectedCompany.name,
                  ModifiedBy: userId,
                  RecordStatusId: 1,
                  Manufacturer: false
                }
              ]
            };
            const saveRes = await Postrequestcall(SAVE_COMPANIES, saveCompanyObj, loginData?.accessToken);
            const newCompanyId = saveRes?.data?.data?.[0]?.objData?.id || saveRes?.data?.[0]?.objData?.id;
            if (newCompanyId) {
              await saveUserCompany(newCompanyId);
              finalCompanyId = newCompanyId;
            }
          } else {
            await saveUserCompany(selectedCompany.id);
            finalCompanyId = selectedCompany.id;
          }
        } catch (companyError) {
          console.error("Onboarding - Error saving company:", companyError);
        }
      }

      // 3. Save User Experience/Work History to Profile
      if (selectedProject) {
        try {
          const workHistoryObj = {
            userId: userId,
            ReturnRecordId: true,
            ReturnRecordError: true,
            SaveList: [
              {
                Id: 0,
                UserId: userId,
                StartDate: startDate ? startDate : null,
                EndDate: present ? null : endDate ? endDate : null,
                Present: present,
                IncludeInResume: true,
                ProjectId: selectedProject?.projectId || 0,
                description: "",
                ProjectCompanyRoleId: 0,
                CompanyId: finalCompanyId || 0,
                RoleId: 0,
                TradeId: 0,
                isFollowing: true,
                ProductIdList: "",
                IsFirstTime: true
              }
            ]
          };
          await Postrequestcall(SAVE_USER_WORK_HISTORY, workHistoryObj, loginData?.accessToken);
        } catch (projectError) {
          console.error("Onboarding - Error saving project work history:", projectError);
        }
      }

    } catch (error) {
      console.error("Error saving onboarding data:", error);
    } finally {
      dispatch(setSelectedsite(null));
      dispatch(setChoosesite(null));
      dispatch(setSelectedProjectdata(null));
      setStartDate("");
      setEndDate("");
      setPresent(true);
      setCompanyStartDate("");
      setCompanyEndDate("");
      setCompanyPresent(true);
      setSavingAll(false);
      dispatch(setLogindata(rawLoginData));
      handleClose();
    }
  };

  return (
    <Box>
      <Modal open={open}>
        <Box className="user-modal-responsive" sx={{ p: 2.5, textAlign: "center", maxHeight: "90vh", overflowY: "auto" }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <img style={{ height: "45px" }} src={BuildIDLogo} alt="BuildID" />
          </Box>

          <Box>
            {step === 1 && (
              <Box>
                <Typography variant="h5" sx={{ fontWeight: "700", color: "#0f172a", mb: 1 }}>
                  Step 1: Add your trades
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select all that apply.
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search for a trade..."
                  value={tradeSearchQuery}
                  onChange={handleTradeSearchChange}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                  }}
                />
                {loadingTrades && allTrades.length === 0 ? (
                  <Box sx={{ py: 4 }}>
                    <CircularProgress size={30} />
                  </Box>
                ) : (
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1.2,
                        justifyContent: "center",
                        maxHeight: "220px",
                        overflowY: "auto",
                        py: 1,
                        mb: 2
                      }}
                    >
                      {allTrades.map((trade) => {
                        const isSelected = selectedTradeIds.includes(trade.id);
                        return (
                          <Chip
                            key={trade.id}
                            label={trade.tradeName}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedTradeIds(selectedTradeIds.filter((id) => id !== trade.id));
                              } else {
                                setSelectedTradeIds([...selectedTradeIds, trade.id]);
                              }
                            }}
                            sx={{
                              bgcolor: isSelected ? "#334155" : "#f1f5f9",
                              color: isSelected ? "#fff" : "#334155",
                              fontWeight: "600",
                              fontSize: "0.85rem",
                              px: 0.5,
                              py: 0.5,
                              cursor: "pointer",
                              "&:hover": {
                                bgcolor: isSelected ? "#1e293b" : "#e2e8f0"
                              }
                            }}
                          />
                        );
                      })}
                    </Box>
                    {hasMoreTrades && (
                      <Button
                        size="small"
                        onClick={loadMoreTrades}
                        disabled={loadingTrades}
                        sx={{ mb: 2, textTransform: "none", color: BIBlue }}
                      >
                        {loadingTrades ? "Loading..." : "Load More Trades"}
                      </Button>
                    )}
                  </Box>
                )}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleSkip}
                    sx={{ py: 1.2, borderRadius: "25px", fontWeight: "700" }}
                  >
                    Skip
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => setStep(2)}
                    sx={{ bgcolor: BIBlue, color: "white", py: 1.2, borderRadius: "25px", fontWeight: "700" }}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}

            {step === 2 && (
              <Box>
                <Typography variant="h5" sx={{ fontWeight: "700", color: "#0f172a", mb: 1 }}>
                  Step 2: What company are you currently working for?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  You can also add it if it's not in BuildID yet.
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Click to select company..."
                  value={selectedCompany?.name || ""}
                  onClick={() => setOpenCompanyModal(true)}
                  InputProps={{
                    readOnly: true,
                    startAdornment: <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />,
                    endAdornment: selectedCompany ? (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCompany(null);
                          setCompanyStartDate("");
                          setCompanyEndDate("");
                          setCompanyPresent(true);
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    ) : null
                  }}
                  sx={{
                    mb: 2,
                    cursor: "pointer",
                    "& input": { cursor: "pointer" }
                  }}
                />

                <CompanySelectModal
                  open={openCompanyModal}
                  onClose={() => setOpenCompanyModal(false)}
                  onSelect={(comp) => {
                    if (comp?.recordStatusId === 3) {
                      alert(comp?.manufacturer ? "This manufacturer is reported" : "This company is reported");
                      return;
                    }
                    setSelectedCompany(comp);
                    setOpenCompanyModal(false);
                  }}
                  selectedId={selectedCompany?.id}
                  title="Select Company"
                  placeholder="Search company..."
                />

                {selectedCompany && (
                  <Box sx={{ mb: 3 }}>
                    {/* Company Date Fields */}
                    <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        type="date"
                        slotProps={{ inputLabel: { shrink: true } }}
                        InputLabelProps={{ shrink: true }}
                        value={companyStartDate}
                        onChange={(e) => setCompanyStartDate(e.target.value)}
                        size="small"
                      />
                      <TextField
                        fullWidth
                        label="End Date"
                        type="date"
                        slotProps={{ inputLabel: { shrink: true } }}
                        InputLabelProps={{ shrink: true }}
                        value={companyEndDate}
                        onChange={(e) => setCompanyEndDate(e.target.value)}
                        disabled={companyPresent}
                        size="small"
                      />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.85rem", color: "#475569", cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={companyPresent}
                          onChange={(e) => {
                            setCompanyPresent(e.target.checked);
                            if (e.target.checked) setCompanyEndDate("");
                          }}
                        />
                        Currently working here
                      </label>
                    </Box>
                  </Box>
                )}
                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setStep(1)}
                    sx={{ py: 1.2, borderRadius: "25px", fontWeight: "700" }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => setStep(3)}
                    sx={{
                      bgcolor: BIBlue,
                      py: 1.2,
                      borderRadius: "25px",
                      fontWeight: "700",
                      "&:hover": { bgcolor: "#1d4ed8" }
                    }}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}

            {/* STEP 3: PROJECT */}
            {step === 3 && (
              <Box>
                <Typography variant="h5" sx={{ fontWeight: "700", color: "#0f172a", mb: 1 }}>
                  Step 3: What project are you currently working at?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Select an active project location from the map.
                </Typography>

                {selectedProject ? (
                  <Box>
                    <Box sx={{ mb: 2, p: 1.5, border: "1px solid #cbd5e1", borderRadius: "12px", display: "flex", alignItems: "center", gap: 2 }}>
                      <Box sx={{ width: "65px", height: "50px", borderRadius: "5px", border: "1px solid #cbd5e1", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <img
                          src={selectedProject?.pictureList?.length > 0 ? selectedProject?.pictureList?.[0]?.thumbUrl : PlaceholderImg}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          alt="Project"
                        />
                      </Box>
                      <Box sx={{ flex: 1, textAlign: "left" }}>
                        <Typography variant="subtitle2" fontWeight="700">
                          {selectedProject.projectName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {selectedProject.siteName} - {selectedProject.address}
                        </Typography>
                      </Box>
                      <IconButton disabled={savingAll} onClick={() => {
                        dispatch(setSelectedProjectdata(null));
                        setStartDate("");
                        setEndDate("");
                        setPresent(true);
                      }}>
                        <CloseIcon />
                      </IconButton>
                    </Box>

                    {/* Date Inputs */}
                    <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        type="date"
                        slotProps={{ inputLabel: { shrink: true } }}
                        InputLabelProps={{ shrink: true }}
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        disabled={savingAll}
                        size="small"
                      />
                      <TextField
                        fullWidth
                        label="End Date"
                        type="date"
                        slotProps={{ inputLabel: { shrink: true } }}
                        InputLabelProps={{ shrink: true }}
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={present || savingAll}
                        size="small"
                      />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
                      <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.85rem", color: "#475569", cursor: savingAll ? "default" : "pointer" }}>
                        <input
                          type="checkbox"
                          checked={present}
                          disabled={savingAll}
                          onChange={(e) => {
                            setPresent(e.target.checked);
                            if (e.target.checked) setEndDate("");
                          }}
                        />
                        Currently working here
                      </label>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    onClick={() => !savingAll && handleSelectSiteClick()}
                    sx={{
                      mb: 4,
                      border: "2px dashed #cbd5e1",
                      borderRadius: "12px",
                      p: 4,
                      textAlign: "center",
                      cursor: savingAll ? "default" : "pointer",
                      bgcolor: "#f8fafc",
                      "&:hover": {
                        bgcolor: savingAll ? "#f8fafc" : "#f1f5f9",
                        borderColor: savingAll ? "#cbd5e1" : BIBlue
                      }
                    }}
                  >
                    <LocationOnIcon sx={{ fontSize: 40, color: BIBlue, mb: 1 }} />
                    <Typography variant="body1" fontWeight="600" color="#475569">
                      Find your project on map
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      by searching address of site and then selecting project
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setStep(2)}
                    disabled={savingAll}
                    sx={{ py: 1.2, borderRadius: "25px", fontWeight: "700" }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleOnboardingFinish}
                    disabled={savingAll}
                    sx={{ bgcolor: BIBlue, color: "white", py: 1.2, borderRadius: "25px", fontWeight: "700" }}
                  >
                    {savingAll ? <CircularProgress size={20} color="inherit" /> : "Jump right in"}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Progress indicators/dots */}
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3, gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: step === 1 ? BIBlue : BIGrey, cursor: savingAll ? "default" : "pointer" }} onClick={() => !savingAll && setStep(1)} />
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: step === 2 ? BIBlue : BIGrey, cursor: savingAll ? "default" : "pointer" }} onClick={() => !savingAll && setStep(2)} />
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: step === 3 ? BIBlue : BIGrey, cursor: savingAll ? "default" : "pointer" }} onClick={() => !savingAll && setStep(3)} />
            </Box>
          </Box>
        </Box>
      </Modal>

      <Mapmodal />
    </Box>
  );
};

export default OnboardingWizard;
